import React, {
  FormEvent,
  useEffect,
  useState,
  memo,
  ReactNode,
  useRef,
  ChangeEvent,
} from "react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import {
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  VersionedTransaction,
} from "@solana/web3.js";
import { TokenInfo } from "@solana/spl-token-registry";
import { APIError, emitter, Icon, Skeleton } from "../../shared";
import { apiClient } from "@lobby/api-client";
import { TokenMapService } from "./helpers";
import { SOL_MINT, TOKEN_PROGRAM_ID, USDC_MINT } from "./const";
import { useTranslate } from "@lobby/ocb-intl";
import { Payment } from "../../entities";
import * as Sentry from "@sentry/react";
import type { Quote, QuoteResult, TokenAccounts } from "./types";
import {
  InputTokenCard,
  OutputTokenCard,
  QuoteDetails,
  SwapButton,
  SwapSuccessDialog,
} from "./components";

interface CryptoSwapTerminalProps {
  publicKey: PublicKey;
  connection: Connection;
  tokenMap: Map<string, TokenInfo>;
  paymentMethodId: number;
}

function CryptoSwapTerminal({ paymentMethodId }: { paymentMethodId: number }) {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <pre>
          An error occurred while loading the crypto swap terminal. Please try
          again later.
        </pre>
      }
    >
      <CryptoSwapTerminalGuard>
        {(publicKey, connection, tokenMap) => (
          <CryptoSwapTerminalView
            publicKey={publicKey}
            connection={connection}
            tokenMap={tokenMap}
            paymentMethodId={paymentMethodId}
          />
        )}
      </CryptoSwapTerminalGuard>
    </Sentry.ErrorBoundary>
  );
}

interface CryptoSwapTerminalGuardProps {
  children: (
    publicKey: PublicKey,
    connection: Connection,
    tokenMap: Map<string, TokenInfo>
  ) => ReactNode;
}

function CryptoSwapTerminalGuard({ children }: CryptoSwapTerminalGuardProps) {
  const [tokenMap, setTokenMap] = useState<Map<string, TokenInfo> | null>(null);

  const { $t } = useTranslate();
  const { connection } = useConnection();
  const { publicKey } = useWallet();

  useEffect(() => {
    TokenMapService.getTokenMap().then((map) => {
      setTokenMap(map);
    });
  }, []);

  return (
    <div className="crypto-swap-terminal relative text-white space-y-5 p-2.5 rounded-[1rem] bg-[#1a1d29]">
      <div className="flex justify-between items-center gap-5">
        <div className="font-bold">
          {$t({ defaultMessage: "Crypto swap terminal" })}
        </div>
        <WalletMultiButton />
      </div>

      {publicKey &&
        connection &&
        tokenMap &&
        tokenMap.size > 0 &&
        children(publicKey, connection, tokenMap)}
    </div>
  );
}

function CryptoSwapTerminalView({
  publicKey,
  connection,
  tokenMap,
  paymentMethodId,
}: CryptoSwapTerminalProps) {
  const { $t } = useTranslate();
  const { signTransaction } = useWallet();
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [isSwapProcessing, setIsSwapProcessing] = useState(false);
  const [isWalletDataLoading, setIsWalletDataLoading] = useState(true);
  const [tokens, setTokens] = useState<TokenAccounts[]>([]);
  const [inputToken, setInputToken] = useState<TokenAccounts | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [outputAmount, setOutputAmount] = useState(1);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const quoteResultRef = useRef<QuoteResult | null>(null);

  const buildSwapMutation = Payment.useBuildSwap();
  const depositMutation = Payment.useDeposit();

  const usdcToken = tokenMap.get(USDC_MINT);
  const isSwapButtonLoadingState = isSwapProcessing || isQuoteLoading;
  const isInsufficientBalance = Boolean(
    quote?.inAmountUi &&
      inputToken?.amount &&
      quote.inAmountUi > inputToken.amount
  );
  const isSwapDisabled =
    !quote ||
    isWalletDataLoading ||
    isSwapButtonLoadingState ||
    outputAmount <= 0 ||
    Boolean(quoteError) ||
    isInsufficientBalance;

  function onTokenSelect(e: ChangeEvent<HTMLInputElement>) {
    if (e.target instanceof HTMLInputElement) {
      setInputToken(tokens[Number(e.target.value)]);
    }

    e.preventDefault();
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!signTransaction) {
      console.error("Wallet doesn't support transaction signing");
      return;
    }

    setIsSwapProcessing(true);

    try {
      const { result: buildSwapResult, error: buildSwapError } =
        await buildSwapMutation.mutateAsync({
          userPublicKey: publicKey.toString(),
          quoteResponse: JSON.stringify(quoteResultRef.current),
        });

      if (buildSwapError) {
        const apiError = new APIError(buildSwapError.message, {
          code: buildSwapError.code,
        });
        emitter.emit("ERROR", { error: apiError });
        console.error("Swap error:", buildSwapError);
        return;
      }

      // console.debug(
      //   `%c == Crypto terminal == %c Swap result `,
      //   "background: #00FF98; color: black",
      //   "background: #FFF468; color: black",
      //   buildSwapResult
      // );

      const transactionBuffer = Uint8Array.from(atob(buildSwapResult), (c) =>
        c.charCodeAt(0)
      );
      const transaction = VersionedTransaction.deserialize(transactionBuffer);
      const signedTransaction = await signTransaction(transaction);
      const signedTransactionBase64 = btoa(
        String.fromCharCode(...signedTransaction.serialize())
      );

      const { result: depositResult, error: depositError } =
        await depositMutation.mutateAsync({
          methodData: {
            swapTransaction: signedTransactionBase64,
          },
          amount: outputAmount * 100,
          methodId: paymentMethodId,
        });

      if (depositError) {
        const apiError = new APIError(depositError.message, {
          code: depositError.code,
        });
        emitter.emit("ERROR", { error: apiError });
        console.error("Deposit error:", depositError);
        return;
      }

      // console.debug(
      //   `%c == Crypto terminal == %c Deposit successful `,
      //   "background: #00FF98; color: black",
      //   "background: #FFF468; color: black",
      //   depositResult
      // );

      const dialog = document.getElementById("cryptoSwapTerminalSuccessDialog");
      if (dialog instanceof HTMLDialogElement) {
        dialog.show();
      }

      getTokenAccounts();
      setOutputAmount(1);
      setQuote(null);
    } catch (error) {
      console.error("Transaction failed:", error);
      window.alert(error);
    } finally {
      setIsSwapProcessing(false);
    }
  }

  async function requestQuote(signal?: AbortSignal) {
    if (!inputToken || !outputAmount || isSwapProcessing) return;

    if (inputToken.address === USDC_MINT) {
      setQuoteError($t({ defaultMessage: "You cannot swap USDC to USDC" }));
      setQuote(null);
      return;
    }

    setQuoteError(null);
    setIsQuoteLoading(true);

    const { result, error } = await apiClient.send<"Payment.getQuote">({
      endpoint: "Payment.getQuote",
      params: {
        inputMint: inputToken.address,
        amount: outputAmount * 100,
      },
      signal,
    });

    if (error) {
      setQuoteError(error.message);
      setIsQuoteLoading(false);
      return;
    }

    quoteResultRef.current = result;
    const inputTokenInfo = tokenMap.get(result.inputMint ?? "");
    const outputTokenInfo = tokenMap.get(result.outputMint ?? "");

    setQuote({
      ...result,
      inAmountUi:
        Number(result.inAmount) / Math.pow(10, inputTokenInfo?.decimals ?? 0),
      outAmountUi:
        Number(result.outAmount) / Math.pow(10, outputTokenInfo?.decimals ?? 0),
    });
    setQuoteError(null);
    setIsQuoteLoading(false);
  }

  async function getTokenAccounts() {
    try {
      setIsWalletDataLoading(true);
      const accountInfo = await connection.getAccountInfo(publicKey);
      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
        publicKey,
        {
          programId: TOKEN_PROGRAM_ID,
        }
      );
      // console.debug(
      //   `%c == Crypto terminal == %c Account info `,
      //   "background: #00FF98; color: black",
      //   "background: #FFF468; color: black",
      //   accountInfo
      // );
      // console.debug(
      //   `%c == Crypto terminal == %c Token accounts `,
      //   "background: #00FF98; color: black",
      //   "background: #FFF468; color: black",
      //   tokenAccounts
      // );
      let solBalance = 0;
      if (accountInfo) {
        solBalance = accountInfo.lamports / LAMPORTS_PER_SOL;
      }

      const tokens = await Promise.all(
        tokenAccounts.value.map(async ({ account }) => {
          const accountInfo = account.data.parsed.info;
          const mint = accountInfo.mint;
          const tokenAmount = accountInfo.tokenAmount.uiAmount;

          const tokenInfo = tokenMap.get(mint);

          return {
            amount: tokenAmount || 0,
            address: tokenInfo?.address || mint,
            name: tokenInfo?.name || "???",
            decimals: tokenInfo?.decimals || 0,
            symbol: tokenInfo?.symbol || "???",
            logoURI: tokenInfo?.logoURI || "",
          };
        })
      );

      if (solBalance > 0) {
        const tokenInfo = tokenMap.get(SOL_MINT);

        if (tokenInfo) {
          tokens.unshift({
            amount: solBalance,
            address: tokenInfo?.address || SOL_MINT,
            name: tokenInfo?.name || "???",
            decimals: tokenInfo?.decimals || 0,
            symbol: tokenInfo?.symbol || "???",
            logoURI: tokenInfo?.logoURI || "",
          });
        }
      }

      setTokens(tokens);
      setInputToken((inputToken) => inputToken || tokens[0]);
    } catch (error) {
      console.error(error);
      setAccountError(error?.toString() || "Failed to load account data");
    } finally {
      setIsWalletDataLoading(false);
    }
  }

  useEffect(() => {
    getTokenAccounts();
  }, [connection, publicKey]);

  useEffect(() => {
    const controller = new AbortController();

    let intervalId: ReturnType<typeof setInterval>;
    const timeoutId = setTimeout(() => {
      requestQuote(controller.signal);
      intervalId = setInterval(requestQuote, 5000, controller.signal);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      controller.abort(
        "Component unmounted or inputToken/outputAmount changed"
      );
    };
  }, [inputToken, outputAmount, isSwapProcessing]);

  useEffect(() => {
    if (outputAmount <= 0) {
      setQuote(null);
      setQuoteError(null);
      setIsQuoteLoading(false);
    } else {
      setIsQuoteLoading(true);
    }
  }, [outputAmount]);

  if (accountError) {
    return (
      <div className="bg-[#242738] rounded-[0.75rem] p-4 ring-1 ring-[#ff565780]">
        {accountError}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2">
        {tokens.length === 0 && !isWalletDataLoading ? (
          <div className="bg-[#242738] rounded-[0.75rem] p-4 ring-1 ring-[#ff565780]">
            <p className="text-center">
              {$t({
                defaultMessage:
                  "Your wallet has no tokens in solana blockchain",
              })}
            </p>
          </div>
        ) : isWalletDataLoading ? (
          <Skeleton className="h-[6.25rem] rounded-[0.75rem]" />
        ) : (
          <InputTokenCard
            token={inputToken}
            quote={quote}
            tokens={tokens}
            onTokenSelect={onTokenSelect}
          />
        )}

        <OutputTokenCard
          outputToken={usdcToken}
          outputAmount={outputAmount}
          onOutputValueChange={setOutputAmount}
        />
      </div>

      <SwapButton
        isLoading={isSwapButtonLoadingState}
        isDisabled={isSwapDisabled}
      >
        {isInsufficientBalance
          ? `Insufficient ${inputToken?.symbol}`
          : $t({ defaultMessage: "Swap" })}
      </SwapButton>

      {quoteError ? (
        <div className="bg-[#242738] rounded-8 p-3 ring-1 ring-[#ff565780] text-[#ff5657] text-sm text-center">
          {quoteError}
        </div>
      ) : (
        <QuoteDetails
          quote={quote}
          inputTokenSymbol={inputToken?.symbol ?? "???"}
        />
      )}

      {isWalletDataLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <span className="absolute animate-spin">
            <Icon name="loading" />
          </span>
        </div>
      )}

      <SwapSuccessDialog />
    </form>
  );
}

export default memo(CryptoSwapTerminal);
